import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, CircleHelp, Compass, Sparkles } from 'lucide-react';
import BrandLink from '@/components/ui/BrandLink';
import Tagline from '@/components/ui/Tagline';
import SeoJsonLd from '@/components/seo/SeoJsonLd';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  buildProgrammaticSchemas,
  getProgrammaticPage,
  PROGRAMMATIC_PAGES,
} from '@/lib/programmaticSeo';
import { createPageUrl } from '@/utils';

function RelatedLinks({ relatedPaths = [] }) {
  const pages = relatedPaths
    .map((path) => getProgrammaticPage(path))
    .filter(Boolean);

  if (!pages.length) return null;

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8">
      <h2 className="text-2xl font-bold text-slate-900 mb-4">Guides associés</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {pages.map((page) => (
          <Link
            key={page.path}
            to={page.path}
            className="group rounded-xl border border-slate-200 bg-white p-4 hover:border-emerald-300 hover:shadow-sm transition-all"
          >
            <p className="font-semibold text-slate-900 group-hover:text-emerald-700 transition-colors">
              {page.heroTitle}
            </p>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{page.description}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function ProgrammaticPage({ page }) {
  const currentPage = page || PROGRAMMATIC_PAGES[0];
  const ctaHref = `${createPageUrl('Register')}?next=${encodeURIComponent(createPageUrl('Onboarding'))}`;
  const pricingHref = createPageUrl('Tarifs');
  const schemas = buildProgrammaticSchemas(currentPage);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-white">
      <SeoJsonLd id={`schema-${currentPage.path.replace(/[^a-z0-9]+/gi, '-')}`} data={schemas} />

      <header className="bg-white border-b border-slate-100 py-4 px-6 sticky top-0 z-20 backdrop-blur supports-[backdrop-filter]:bg-white/90">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <BrandLink />
          <div className="hidden md:flex items-center gap-6 text-sm text-slate-600">
            <Link to="/" className="hover:text-slate-900">Accueil</Link>
            <Link to={pricingHref} className="hover:text-slate-900">Tarifs</Link>
            <Link to={createPageUrl('FAQ')} className="hover:text-slate-900">FAQ</Link>
            <Link to={createPageUrl('Contact')} className="hover:text-slate-900">Contact</Link>
          </div>
          <Button asChild className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
            <Link to={ctaHref}>Créer un compte</Link>
          </Button>
        </div>
      </header>

      <main className="px-4 py-10 md:py-14">
        <div className="max-w-6xl mx-auto space-y-10">
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-slate-100 rounded-3xl shadow-sm p-6 md:p-10"
          >
            <div className="max-w-4xl">
              <Breadcrumb className="mb-5">
                <BreadcrumbList className="text-slate-500">
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to="/">Accueil</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage className="text-slate-700">{currentPage.heroTitle}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>

              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 md:p-5 mb-6">
                <p className="text-sm font-semibold text-emerald-900 mb-3">Sommaire</p>
                <div className="flex flex-wrap gap-2">
                  <a href="#difficultes" className="inline-flex items-center rounded-full bg-white px-3 py-1.5 text-sm text-slate-700 border border-emerald-100 hover:border-emerald-300 hover:text-emerald-700 transition-colors">Difficultés</a>
                  <a href="#benefices" className="inline-flex items-center rounded-full bg-white px-3 py-1.5 text-sm text-slate-700 border border-emerald-100 hover:border-emerald-300 hover:text-emerald-700 transition-colors">Bénéfices</a>
                  <a href="#fonctionnalites" className="inline-flex items-center rounded-full bg-white px-3 py-1.5 text-sm text-slate-700 border border-emerald-100 hover:border-emerald-300 hover:text-emerald-700 transition-colors">Fonctionnalités</a>
                  <a href="#faq" className="inline-flex items-center rounded-full bg-white px-3 py-1.5 text-sm text-slate-700 border border-emerald-100 hover:border-emerald-300 hover:text-emerald-700 transition-colors">FAQ</a>
                </div>
              </div>
              <Badge className="mb-4 bg-emerald-50 text-emerald-700 border-emerald-200">
                {currentPage.heroBadge}
              </Badge>
              <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900 mb-5">
                {currentPage.heroTitle}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-3xl">
                {currentPage.heroDescription}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                {currentPage.summaryPoints.map((point) => (
                  <div key={point} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <p className="text-sm font-medium text-slate-800">{point}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Button asChild size="lg" className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
                  <Link to={ctaHref}>
                    Essayer TrésoPilot
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to={pricingHref}>Voir les tarifs</Link>
                </Button>
              </div>
            </div>
          </motion.section>

          <section id="difficultes" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-100 rounded-3xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <Compass className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900">Les difficultés fréquentes</h2>
              </div>
              <div className="space-y-4">
                {currentPage.painPoints.map((point) => (
                  <div key={point} className="rounded-2xl bg-rose-50 border border-rose-100 p-4 text-slate-700 leading-relaxed">
                    {point}
                  </div>
                ))}
              </div>
            </div>

            <div id="benefices" className="bg-white border border-slate-100 rounded-3xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900">Ce que cette approche vous apporte</h2>
              </div>
              <div className="space-y-4">
                {currentPage.benefits.map((point) => (
                  <div key={point} className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 text-slate-700 leading-relaxed">
                    {point}
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="fonctionnalites" className="bg-white border border-slate-100 rounded-3xl p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">{currentPage.featureTitle}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {currentPage.featureBullets.map((feature) => (
                <div key={feature} className="rounded-2xl border border-slate-200 p-5 bg-slate-50">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <p className="text-slate-700 leading-relaxed">{feature}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {currentPage.sections.map((section) => (
              <div key={section.title} className="bg-white border border-slate-100 rounded-3xl p-6 md:p-8">
                <h2 className="text-xl font-bold text-slate-900 mb-3">{section.title}</h2>
                <p className="text-slate-600 leading-relaxed">{section.body}</p>
              </div>
            ))}
          </section>

          {Array.isArray(currentPage.faq) && currentPage.faq.length > 0 && (
            <section id="faq" className="bg-white border border-slate-100 rounded-3xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <CircleHelp className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Questions fréquentes</h2>
              </div>
              <Accordion type="single" collapsible className="space-y-3">
                {currentPage.faq.map((item, index) => (
                  <AccordionItem key={item.q} value={`faq-${index}`} className="bg-slate-50 rounded-xl px-4 border-0">
                    <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-700 leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          )}

          <RelatedLinks relatedPaths={currentPage.relatedPaths} />

          <section className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-3xl p-8 md:p-10 text-white text-center">
            <h2 className="text-3xl font-bold mb-3">Passez du flou à la visibilité</h2>
            <p className="text-emerald-100 max-w-3xl mx-auto leading-relaxed">
              TrésoPilot a été pensé pour aider les indépendants à voir ce qui entre, ce qui doit être provisionné et ce qu’ils peuvent réellement garder.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
              <Button asChild size="lg" className="bg-white text-emerald-700 hover:bg-slate-100">
                <Link to={ctaHref}>
                  Commencer gratuitement
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" className="border border-white/70 bg-emerald-900/20 text-white shadow-sm hover:bg-emerald-950/30 hover:border-white">
                <Link to={createPageUrl('Contact')}>Parler à l’équipe</Link>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <footer className="bg-slate-900 px-4 py-12 mt-8">
        <div className="max-w-6xl mx-auto text-center space-y-5">
          <BrandLink to="/" variant="light" />
          <div className="flex flex-wrap justify-center gap-3 text-sm">
            <Link to="/" className="text-slate-400 hover:text-white transition-colors">Accueil</Link>
            <span className="text-slate-600">•</span>
            <Link to={pricingHref} className="text-slate-400 hover:text-white transition-colors">Tarifs</Link>
            <span className="text-slate-600">•</span>
            <Link to={createPageUrl('FAQ')} className="text-slate-400 hover:text-white transition-colors">FAQ</Link>
            <span className="text-slate-600">•</span>
            <Link to={createPageUrl('Contact')} className="text-slate-400 hover:text-white transition-colors">Contact</Link>
          </div>
          <p className="text-emerald-200 text-sm">
            <Tagline className="text-emerald-200" />
          </p>
          <p className="text-slate-400 text-sm">© {new Date().getFullYear()} TrésoPilot. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
}
